/**
 * BioController.js - Controls the Bio button and panel functionality
 *
 * This module handles showing/hiding the bio button and panel,
 * loading character bio data, and implementing dragging functionality.
 */

import * as THREE from "three";
import { BioDataManager } from "./BioDataManager.js";
import { ModelManager } from "./ModelManager.js";
import { characterData } from "./characterData.js";
import { ModelViewer } from "./ModelViewer.js";
import { BioSectionCollapse } from "./BioSectionCollapse.js";

export class BioController {
    constructor() {
        // Initialize UI elements
        this.bioButton = document.getElementById("bio-button");
        this.bioButtonImage = document.getElementById("bio-button-image");
        this.bioPanel = document.getElementById("bio-panel");
        this.bioPanelTitle = document.getElementById("bio-character-name");
        this.bioPanelClose = document.getElementById("bio-panel-close");

        // Ensure bio button has pulse animation class on initialization
        if (this.bioButton) {
            this.bioButton.classList.add("bio-button-pulse");
        }

        // Initialize Bio Data Manager
        this.bioDataManager = new BioDataManager();

        // Initialize ModelManager for direct access to model data
        this.modelManager = new ModelManager();

        // Get the global ModelViewer instance for camera control
        this.modelViewer = null;
        this.findModelViewer();

        // Panel state tracking
        this.isPanelActive = false;

        // Current character info
        this.currentCharacterId = null;
        this.currentCharacterName = null;

        // Try to get the current character ID from ModelManager first (most reliable)
        try {
            const currentModelId = this.modelManager.getCurrentModelId();
            if (currentModelId) {
                console.log(
                    `BioController: Setting initial model ID from ModelManager: ${currentModelId}`,
                );
                this.currentCharacterId = currentModelId;

                // Try to load additional info from characterData if available
                if (
                    window.characterData &&
                    window.characterData[currentModelId]
                ) {
                    this.currentCharacterName =
                        window.characterData[currentModelId].name;
                    console.log(
                        `BioController: Found initial character name: ${this.currentCharacterName}`,
                    );
                }
            } else {
                // Fallback to localStorage
                const storedModelId = localStorage.getItem("selectedModelId");
                if (storedModelId) {
                    console.log(
                        `BioController: Setting initial model ID from localStorage: ${storedModelId}`,
                    );
                    this.currentCharacterId = storedModelId;

                    // Try to load additional info from characterData if available
                    if (
                        window.characterData &&
                        window.characterData[storedModelId]
                    ) {
                        this.currentCharacterName =
                            window.characterData[storedModelId].name;
                        console.log(
                            `BioController: Found initial character name: ${this.currentCharacterName}`,
                        );
                    }
                }
            }
        } catch (error) {
            console.error(
                "BioController: Error initializing model data:",
                error,
            );
        }

        // Auto-close threshold - percentage of panel width for swipe gesture
        this.closeThreshold = 0.3;

        // Initialize event listeners
        this.initEventListeners();

        // Try to set initial bio button image if we have a character ID
        if (this.currentCharacterId) {
            this.updateBioButtonImage();
        }

        // Initialize the collapsible sections functionality
        this.bioSectionCollapse = new BioSectionCollapse();

        // Listen for character change events to update bio data
        document.addEventListener("character-changed", (event) => {
            const { modelId, name, imageUrl } = event.detail;
            if (modelId) {
                // Drop any stale book context from a previously read book so
                // it can never leak into the new character's chat prompt.
                if (window.ActiveBookContext &&
                    window.ActiveBookContext.character_id !== modelId) {
                    window.ActiveBookContext = null;
                    window.dispatchEvent(new CustomEvent("bookContextChanged", { detail: null }));
                }
                // If the EPUB reader is open for a different character, close it.
                try {
                    const r = window.getEpubReader && window.getEpubReader();
                    if (r && r.isOpen && r.activeCharacterId !== modelId) {
                        r.close();
                    }
                } catch (_) {}

                this.setCharacter(modelId, name, imageUrl);
                console.log(
                    `Bio panel updated for character: ${name} (${modelId})`,
                );
            }
        });

        // Listen for chat and story section opening while bio section is active
        this.setupInterfaceListeners();

        // Render the per-character "Book" card on initial load.
        if (this.currentCharacterId) {
            try { this.refreshBookList(); } catch (_) {}
        }

        // Also refresh when the bio panel is opened.
        document.addEventListener("bioPanelOpened", () => {
            try {
                if (!this.currentCharacterId) {
                    const stored = localStorage.getItem("selectedModelId");
                    if (stored) this.currentCharacterId = stored;
                }
                this.refreshBookList();
            } catch (err) {
                console.warn("BioController: bioPanelOpened refresh failed", err);
            }
        });

        // Log the initial state
        console.log("Bio Controller initialized", {
            currentCharacterId: this.currentCharacterId,
            currentCharacterName: this.currentCharacterName,
        });
    }

    /**
     * Find and store the ModelViewer instance
     * This is needed for camera animation
     */
    findModelViewer() {
        // Try to get the ModelViewer instance from the window
        // Look for it in the main.js, which typically initializes and stores it
        try {
            // Wait a moment for the ModelViewer to be initialized
            setTimeout(() => {
                // Check if window object has a modelViewer reference
                if (window.modelViewer) {
                    this.modelViewer = window.modelViewer;
                    console.log(
                        "BioController: Found ModelViewer instance in window",
                    );
                } else {
                    // Try to find the canvas element
                    const viewerCanvas = document.getElementById("viewer");
                    if (viewerCanvas) {
                        // The ModelViewer might be in the canvas's __modelViewer property
                        if (viewerCanvas.__modelViewer) {
                            this.modelViewer = viewerCanvas.__modelViewer;
                            console.log(
                                "BioController: Found ModelViewer instance in canvas",
                            );
                        } else {
                            console.warn(
                                "BioController: ModelViewer instance not found in canvas",
                            );
                        }
                    } else {
                        console.warn("BioController: Viewer canvas not found");
                    }
                }
            }, 500); // Wait 500ms for everything to initialize
        } catch (error) {
            console.error("BioController: Error finding ModelViewer:", error);
        }
    }

    /**
     * Setup listener to detect when chat or story section opens while bio section is active
     * This will reset the bio section camera animation to avoid conflicts
     */
    setupInterfaceListeners() {
        try {
            // Find or wait for the chat container to be available
            const findChatContainer = () => {
                const chatContainer = document.querySelector(".chat-container");
                if (chatContainer) {
                    this.observeElementVisibility(chatContainer, "chat");
                } else {
                    // Chat container might not be ready yet, try again after a short delay
                    setTimeout(findChatContainer, 100);
                }
            };

            // Find or wait for the story showcase modal to be available
            const findStoryModal = () => {
                const storyModal = document.querySelector(
                    "#story-showcase-modal",
                );
                if (storyModal) {
                    this.observeElementVisibility(storyModal, "story");
                } else {
                    // Story modal might not be ready yet, try again after a short delay
                    setTimeout(findStoryModal, 100);
                }
            };

            findChatContainer();
            findStoryModal();
        } catch (error) {
            console.error(
                "BioController: Error setting up interface listeners:",
                error,
            );
        }
    }

    /**
     * Observe changes to element visibility for both chat and story sections
     * @param {HTMLElement} element - The element to observe
     * @param {string} type - Type of element ('chat' or 'story')
     */
    observeElementVisibility(element, type) {
        // Create a MutationObserver to watch for class changes on the element
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (
                    mutation.type === "attributes" &&
                    mutation.attributeName === "class"
                ) {
                    const target = mutation.target;
                    const hasVisibleClass =
                        target.classList.contains("visible");
                    const previouslyVisible =
                        mutation.oldValue &&
                        mutation.oldValue.includes("visible");

                    // Element just became visible (wasn't visible before, now it is)
                    if (hasVisibleClass && !previouslyVisible) {
                        this.onInterfaceOpened(type);
                    }
                }
            });
        });

        // Start observing the element for attribute changes
        observer.observe(element, {
            attributes: true,
            attributeFilter: ["class"],
            attributeOldValue: true,
        });

        console.log(`BioController: ${type} visibility observer set up`);
    }

    /**
     * Handle when chat or story section is opened
     * If bio section is currently active, reset the camera animation
     * @param {string} type - Type of interface that opened ('chat' or 'story')
     */
    onInterfaceOpened(type) {
        console.log(
            `BioController: ${type} section opened, checking if bio section is active`,
        );

        // Check if bio panel is currently active/open
        if (
            this.isPanelActive &&
            this.bioPanel &&
            !this.bioPanel.classList.contains("d-none")
        ) {
            console.log(
                `BioController: Bio section is active when ${type} opened, resetting camera animation`,
            );

            // Reset the bio section camera animation
            this.resetBioCameraForInterfaceOpening(type);
        }
    }

    /**
     * Reset bio camera animation specifically when chat or story section opens
     * This is different from normal reset as we want to ensure it happens even if other interfaces are visible
     * @param {string} type - Type of interface that opened ('chat' or 'story')
     */
    resetBioCameraForInterfaceOpening(type) {
        // Find the modelViewer instance if it wasn't found
        if (!this.modelViewer) {
            if (window.modelViewer) {
                this.modelViewer = window.modelViewer;
            } else {
                const viewerCanvas = document.getElementById("viewer");
                if (viewerCanvas && viewerCanvas.__modelViewer) {
                    this.modelViewer = viewerCanvas.__modelViewer;
                }
            }
        }

        if (this.modelViewer) {
            try {
                console.log(
                    `BioController: Resetting camera due to ${type} opening while bio active`,
                );

                const camera = this.modelViewer.camera;
                const controls = this.modelViewer.controls;

                if (camera && controls) {
                    // Use stored original position if available, otherwise use default
                    let targetPos, targetTarget;

                    if (this.originalCameraPos && this.originalTargetPos) {
                        targetPos = this.originalCameraPos.clone();
                        targetTarget = this.originalTargetPos.clone();
                        console.log(
                            "BioController: Using stored original camera position",
                        );
                    } else {
                        // Fallback to default camera position
                        targetPos = new THREE.Vector3(0.0, 150.0, 110.6);
                        targetTarget = new THREE.Vector3(0, 150, 0);
                        console.log(
                            "BioController: Using default camera position",
                        );
                    }

                    // Smooth transition back to original/default position
                    const duration = 800; // Slightly faster transition
                    const startPos = camera.position.clone();
                    const startTarget = controls.target.clone();
                    const startTime = Date.now();

                    // Disable controls during transition
                    controls.enabled = false;

                    const animate = () => {
                        const elapsed = Date.now() - startTime;
                        const progress = Math.min(elapsed / duration, 1);

                        // Ease out for smoother motion
                        const t = 1 - Math.pow(1 - progress, 3);

                        // Interpolate position and target
                        camera.position.lerpVectors(startPos, targetPos, t);
                        controls.target.lerpVectors(
                            startTarget,
                            targetTarget,
                            t,
                        );

                        camera.lookAt(controls.target);
                        controls.update();

                        if (progress < 1) {
                            requestAnimationFrame(animate);
                        } else {
                            // Set exact positions and re-enable controls
                            camera.position.copy(targetPos);
                            controls.target.copy(targetTarget);
                            camera.lookAt(controls.target);
                            controls.update();
                            controls.enabled = true;

                            console.log(
                                `BioController: Camera reset complete for ${type} opening`,
                            );
                        }
                    };

                    // Start animation
                    requestAnimationFrame(animate);
                }
            } catch (error) {
                console.error(
                    `BioController: Error resetting camera for ${type} opening:`,
                    error,
                );
                // Re-enable controls if there was an error
                if (this.modelViewer.controls) {
                    this.modelViewer.controls.enabled = true;
                }
            }
        } else {
            console.warn(
                "BioController: Cannot reset camera - ModelViewer not available",
            );
        }
    }

    /**
     * Start the camera animation sequence
     */
    startCameraAnimation() {
        // Check if chat window is active/open - if so, skip camera animation
        const chatContainer = document.querySelector(".chat-container");
        const isChatActive =
            chatContainer && chatContainer.classList.contains("visible");

        if (isChatActive) {
            console.log(
                "BioController: Chat window is active, skipping camera animation to avoid disrupting user interaction",
            );
            return;
        }

        // Find the modelViewer instance if it wasn't found during initialization
        if (!this.modelViewer) {
            // Try to find the global ModelViewer instance
            if (window.modelViewer) {
                this.modelViewer = window.modelViewer;
                console.log("BioController: Found modelViewer in window");
            } else {
                const viewerCanvas = document.getElementById("viewer");
                if (viewerCanvas && viewerCanvas.__modelViewer) {
                    this.modelViewer = viewerCanvas.__modelViewer;
                    console.log(
                        "BioController: Found modelViewer in canvas element",
                    );
                }
            }
        }

        // If we have a ModelViewer, play the bio camera sequence
        if (this.modelViewer) {
            // Try to play the animation sequence
            try {
                console.log(
                    "BioController: Starting bio camera animation sequence",
                );

                // Basic camera movement - direct approach as fallback
                // Get the camera and controls
                const camera = this.modelViewer.camera;
                const controls = this.modelViewer.controls;

                if (camera && controls) {
                    console.log(
                        "BioController: Performing simple camera animation",
                    );

                    // Store original positions to return to
                    // Save the original camera position when opening bio panel
                    this.originalCameraPos = camera.position.clone();
                    this.originalTargetPos = controls.target.clone();

                    // Disable controls during animation
                    controls.enabled = false;

                    // Animate camera to zoom in position
                    // This is a simple version that will be replaced by the full animation
                    // when the CameraAnimator is working correctly

                    // Create a smooth camera transition to bio view position
                    // Start with current position
                    const startPos = camera.position.clone();
                    const startTarget = controls.target.clone();

                    // End position - on right side so character faces the bio panel
                    // Keep the current Y position instead of changing it
                    const currentY = startPos.y; // Maintain current Y position
                    const currentTargetY = startTarget.y; // Maintain current target Y position
                    const endPos = new THREE.Vector3(-40, currentY, 80);
                    const endTarget = new THREE.Vector3(0, currentTargetY, 0);

                    // Animation parameters
                    const duration = 1000; // 1 second for smooth transition
                    const startTime = Date.now();

                    // Animation function for smooth transition
                    const animateCameraMove = () => {
                        const elapsed = Date.now() - startTime;
                        const progress = Math.min(elapsed / duration, 1);

                        // Use cubic easing for smoother motion
                        const t = 1 - Math.pow(1 - progress, 3);

                        // Interpolate position
                        camera.position.lerpVectors(startPos, endPos, t);

                        // Interpolate target
                        controls.target.lerpVectors(startTarget, endTarget, t);

                        // Update camera
                        camera.lookAt(controls.target);
                        controls.update();

                        // Continue animation if not complete
                        if (progress < 1) {
                            requestAnimationFrame(animateCameraMove);
                        } else {
                            // Re-enable controls when animation completes
                            controls.enabled = true;
                            console.log(
                                "BioController: Camera transition to bio view complete",
                            );
                        }
                    };

                    // Start the camera animation
                    requestAnimationFrame(animateCameraMove);
                }

                // We're not using the CameraAnimator anymore, just our custom smooth transition above
            } catch (error) {
                console.error(
                    "BioController: Error starting camera animation:",
                    error,
                );
            }
        } else {
            console.warn(
                "BioController: Cannot start camera animation, ModelViewer not available",
            );
        }
    }

    /**
     * Reset the camera to its default position
     * Called when the bio panel is closed
     */
    resetCameraPosition() {
        // Check if chat window is active/open - if so, skip camera reset animation
        const chatContainer = document.querySelector(".chat-container");
        const isChatActive =
            chatContainer && chatContainer.classList.contains("visible");

        if (isChatActive) {
            console.log(
                "BioController: Chat window is active, skipping camera reset to avoid disrupting user interaction",
            );
            return;
        }

        // Find the modelViewer instance if it wasn't found
        if (!this.modelViewer) {
            if (window.modelViewer) {
                this.modelViewer = window.modelViewer;
            } else {
                const viewerCanvas = document.getElementById("viewer");
                if (viewerCanvas && viewerCanvas.__modelViewer) {
                    this.modelViewer = viewerCanvas.__modelViewer;
                }
            }
        }

        if (this.modelViewer) {
            try {
                console.log(
                    "BioController: Resetting camera to default position",
                );

                const camera = this.modelViewer.camera;
                const controls = this.modelViewer.controls;

                if (camera && controls) {
                    // Check if we stored the original position when opening the panel
                    if (this.originalCameraPos && this.originalTargetPos) {
                        // Smooth transition back to original position
                        const duration = 1000; // 1 second transition
                        const startPos = camera.position.clone();
                        const startTarget = controls.target.clone();
                        const startTime = Date.now();

                        // Store final positions to ensure exact positioning
                        const endPos = this.originalCameraPos.clone();
                        const endTarget = this.originalTargetPos.clone();

                        // Disable controls during transition
                        controls.enabled = false;

                        const animate = () => {
                            const elapsed = Date.now() - startTime;
                            const progress = Math.min(elapsed / duration, 1);

                            // Ease out cubic for smoother motion
                            const t = 1 - Math.pow(1 - progress, 3);

                            // Interpolate position
                            camera.position.lerpVectors(startPos, endPos, t);

                            // Interpolate target
                            controls.target.lerpVectors(
                                startTarget,
                                endTarget,
                                t,
                            );

                            camera.lookAt(controls.target);
                            controls.update();

                            if (progress < 1) {
                                requestAnimationFrame(animate);
                            } else {
                                // Set exact positions at the end to avoid floating point errors
                                camera.position.copy(endPos);
                                controls.target.copy(endTarget);
                                camera.lookAt(controls.target);
                                controls.update();

                                // Re-enable controls when done
                                controls.enabled = true;
                                console.log(
                                    "BioController: Camera reset complete with exact original position",
                                );
                            }
                        };

                        // Start animation
                        requestAnimationFrame(animate);
                    } else {
                        // If we don't have the original position, just reset to a default position
                        // This is a fallback in case the original position wasn't saved
                        console.warn(
                            "BioController: Original camera position not found, using default",
                        );

                        // Default position and target
                        camera.position.set(0.0, 150.0, 110.6);
                        controls.target.set(0, 150, 0);
                        camera.lookAt(controls.target);
                        controls.update();
                        controls.enabled = true;
                    }
                }

                // We're not using the CameraAnimator anymore for reset,
                // since we're handling it directly with our custom smooth transition
            } catch (error) {
                console.error(
                    "BioController: Error resetting camera position:",
                    error,
                );
            }
        }
    }

    /**
     * Handle the Send to Chat button click event
     * This method provides a direct way to handle sending bio data to chat
     * while ensuring the camera position is properly reset
     *
     * @param {string} textToSend - The text to send to the chat input
     */
    handleSendToChat(textToSend) {
        console.log("BioController: Handling Send to Chat button click");

        // First find the camera and store its current position if not already saved
        this.findModelViewer();
        if (
            this.modelViewer &&
            !this.originalCameraPos &&
            !this.originalTargetPos
        ) {
            try {
                // First make sure we have a reference to the original camera position
                // in case it wasn't captured when opening the panel
                const camera = this.modelViewer.camera;
                const controls = this.modelViewer.controls;

                if (camera && controls) {
                    console.log(
                        "BioController: Capturing current camera position for Send to Chat",
                    );
                    // Store the current position to return to
                    this.originalCameraPos = new THREE.Vector3(
                        0.0,
                        150.0,
                        110.6,
                    ); // Default position
                    this.originalTargetPos = new THREE.Vector3(0, 150, 0); // Default target
                }
            } catch (error) {
                console.error("Error storing camera position:", error);
            }
        }

        // Close the bio panel with animation
        this.toggleBioPanel(false);

        // Use a short delay to make sure toggleBioPanel completes first
        setTimeout(() => {
            // Force an immediate camera reset with high priority - after panel starts closing
            console.log(
                "BioController: Forcing camera reset from Send to Chat button",
            );
            this.resetCameraPosition();

            // Ensure camera position is definitely reset to default even if animation fails
            setTimeout(() => {
                // Double-check camera position
                if (this.modelViewer) {
                    const camera = this.modelViewer.camera;
                    const controls = this.modelViewer.controls;

                    if (camera && controls) {
                        // Make absolutely sure we're at the default position
                        camera.position.set(0.0, 160.0, 110.6);
                        controls.target.set(0, 160, 0);
                        camera.lookAt(controls.target);
                        controls.update();
                        console.log(
                            "BioController: Camera position confirmed at default position",
                        );
                    }
                }
            }, 1200); // Wait longer than the animation duration
        }, 100);

        // Make sure chat container is visible
        const chatContainer = document.querySelector(".chat-container");
        if (chatContainer) {
            chatContainer.classList.add("visible");
        }

        // Make chat interface visible if it's not already
        const chatInterface = document.querySelector(".chat-interface");
        if (chatInterface) {
            chatInterface.style.display = "block";
        }

        console.log("BioController: Sending to chat directly:", textToSend);

        // Find the chatController to send the message directly
        if (window.chatController) {
            // Use the ChatController to send the message directly
            window.chatController.sendMessage(textToSend);
        } else {
            // Fallback to the old method if ChatController is not available
            const chatInput = document.getElementById("chat-input");
            if (chatInput) {
                // Set the text and focus
                chatInput.value = textToSend;
                chatInput.focus();

                // Trigger input event for height adjustment
                const inputEvent = new Event("input", { bubbles: true });
                chatInput.dispatchEvent(inputEvent);

                // Trigger a focus event to ensure any event listeners for chat UI are triggered
                const focusEvent = new Event("focus", { bubbles: true });
                chatInput.dispatchEvent(focusEvent);

                // Simulate clicking the send button
                const sendButton = document.getElementById("send-button");
                if (sendButton) {
                    sendButton.click();
                }
            }
        }
    }

    initEventListeners() {
        // Bio button click listener - opens bio panel only (no toggle functionality)
        this.bioButton.addEventListener("click", () => {
            // Stop the pulse animation after first interaction
            this.bioButton.classList.add("interaction-done");

            // Mark timeline event as read when bio button is clicked
            if (window.timelineEventManager) {
                window.timelineEventManager.markEventAsRead();
            }

            // Start camera animation sequence
            this.startCameraAnimation();

            // Always set panel to active when bio button is clicked
            this.isPanelActive = true;

            // Get the model ID
            let modelId = null;

            // First try from ModelManager (most reliable)
            try {
                modelId = this.modelManager.getCurrentModelId();
                if (modelId) {
                    console.log(
                        `BioController: Bio button clicked, current model ID from ModelManager: ${modelId}`,
                    );
                }
            } catch (error) {
                console.warn(
                    "BioController: Error getting model ID from ModelManager:",
                    error,
                );
            }

            // Fallback to localStorage if necessary
            if (!modelId) {
                modelId = localStorage.getItem("selectedModelId");
                if (modelId) {
                    console.log(
                        `BioController: Bio button clicked, current model ID from localStorage: ${modelId}`,
                    );
                }
            }

            // Fallback to window globals if available
            if (!modelId && window.currentModelId) {
                modelId = window.currentModelId;
                console.log(
                    `BioController: Bio button clicked, current model ID from window global: ${modelId}`,
                );
            }

            // If we found a model ID from any source, use it
            if (modelId) {
                this.currentCharacterId = modelId;

                // Try to get more details from character data
                if (window.characterData && window.characterData[modelId]) {
                    this.currentCharacterName =
                        window.characterData[modelId].name;
                }

                // Update the Bio button image with the current character's image
                this.updateBioButtonImage();
            } else {
                console.warn(
                    "BioController: Bio button clicked, but no model ID found in any source",
                );
            }

            // Open the panel
            this.toggleBioPanel(true);
        });

        // Close button click listener
        this.bioPanelClose.addEventListener("click", () => {
            this.isPanelActive = false;
            this.toggleBioPanel(false);

            // Return the camera to default position when bio panel is closed
            this.resetCameraPosition();
        });

        // Get the panel header element - no longer used for dragging
        this.panelHeader = document.querySelector(".bio-panel-header");
    }

    /**
     * Set the character for the bio button and panel
     *
     * @param {string} characterId - UUID of the character
     * @param {string} characterName - Name of the character
     * @param {string} imageUrl - URL to the character's image
     */
    setCharacter(characterId, characterName, imageUrl) {
        this.currentCharacterId = characterId;
        this.currentCharacterName = characterName;

        // Update bio button image
        if (this.bioButtonImage && imageUrl) {
            this.bioButtonImage.src = imageUrl;
            this.bioButtonImage.alt = `${characterName} Bio`;
        }

        // Update bio panel title
        if (this.bioPanelTitle) {
            this.bioPanelTitle.textContent = characterName;
        }

        // Load bio data
        this.loadBioData();

        // Refresh per-character book list (Orwell etc.)
        this.refreshBookList();
    }

    /**
     * Show / hide the BIO "Books" section based on the per-character book
     * registry. When one or more books are registered, render a card per
     * book in #bio-books-list and wire each one to open the EPUB reader
     * for that specific book.
     */
    refreshBookList() {
        const section = document.getElementById("bio-book-section");
        const listEl = document.getElementById("bio-books-list");
        if (!section || !listEl || !this.currentCharacterId) return;

        const reader = (typeof window.getEpubReader === "function")
            ? window.getEpubReader()
            : null;
        if (!reader) {
            section.style.display = "none";
            return;
        }

        const characterId = this.currentCharacterId;
        const characterName = this.currentCharacterName;
        const fallbackCover = (this.bioButtonImage && this.bioButtonImage.src) || "";

        reader.getBooksForCharacter(characterId).then((books) => {
            if (characterId !== this.currentCharacterId) return; // stale
            if (!books || !books.length) {
                section.style.display = "none";
                listEl.innerHTML = "";
                return;
            }
            section.style.display = "";
            listEl.innerHTML = "";
            books.forEach((book) => {
                const cover = book.cover || fallbackCover;
                const author = book.author || characterName || "";
                const card = document.createElement("div");
                card.className = "bio-book-card";
                card.tabIndex = 0;
                card.setAttribute("role", "button");
                card.setAttribute("aria-label", `Open ${book.title || "book"} reader`);
                card.innerHTML = `
                    <img class="bio-book-cover" src="${cover}" alt="" onerror="this.style.visibility='hidden'" />
                    <div class="bio-book-meta">
                        <div class="bio-book-title"></div>
                        <div class="bio-book-author"></div>
                        <div class="bio-book-cta"><i class="fas fa-book-open"></i>Read now</div>
                    </div>
                `;
                card.querySelector(".bio-book-title").textContent = book.title || "Untitled";
                card.querySelector(".bio-book-author").textContent = author;
                const bookTitle = book.title || "Untitled";
                const open = () => {
                    const r = window.getEpubReader && window.getEpubReader();
                    if (!r) return;
                    // Put the right panel into REAL-reader mode BEFORE opening so
                    // the pretty toolbar (text size, contents, scroll, theme, page
                    // nav) drives the engine and the mockup page is hidden. Opening
                    // from the Bio "Books" shelf used to skip this, leaving
                    // isReal()=false in the bridge — so every control silently
                    // no-opped on the real book and the mockup page stacked behind
                    // it (the "pages look broken" symptom).
                    const panel = document.getElementById("right-panel");
                    if (panel) {
                        panel.dataset.view = "reader";
                        panel.dataset.realBook = "1";
                    }
                    const titleEl = document.getElementById("panel-title");
                    if (titleEl) titleEl.textContent = bookTitle;
                    try {
                        const root = document.documentElement;
                        const cw = parseInt(getComputedStyle(root).getPropertyValue("--panel-w"), 10) || 380;
                        if (cw < 520) root.style.setProperty("--panel-w", "520px");
                    } catch (_) {}
                    document.body.classList.add("panel-open");
                    window.__realReaderEntry = "bio";
                    r.open(characterId, characterName, book.id);
                };
                card.addEventListener("click", open);
                card.addEventListener("keydown", (e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); }
                });
                listEl.appendChild(card);
            });
        }).catch((err) => {
            console.warn("BioController: refreshBookList failed", err);
            section.style.display = "none";
        });
    }

    /**
     * Load bio data for the current character
     */
    loadBioData() {
        // First, make a final attempt to get the character ID if not already set
        if (!this.currentCharacterId) {
            console.warn(
                "BioController: No character ID set, attempting to get from multiple sources",
            );

            // Try from ModelManager first
            try {
                const currentModelId = this.modelManager.getCurrentModelId();
                if (currentModelId) {
                    this.currentCharacterId = currentModelId;
                    console.log(
                        `BioController: Retrieved model ID from ModelManager: ${currentModelId}`,
                    );
                }
            } catch (error) {
                console.warn(
                    "BioController: Error getting model ID from ModelManager:",
                    error,
                );
            }

            // Fallback to localStorage
            if (!this.currentCharacterId) {
                const storedModelId = localStorage.getItem("selectedModelId");
                if (storedModelId) {
                    this.currentCharacterId = storedModelId;
                    console.log(
                        `BioController: Retrieved model ID from localStorage: ${storedModelId}`,
                    );
                }
            }

            // Fallback to window globals if available
            if (!this.currentCharacterId && window.currentModelId) {
                this.currentCharacterId = window.currentModelId;
                console.log(
                    `BioController: Retrieved model ID from window global: ${window.currentModelId}`,
                );
            }

            // Check if we found any model ID
            if (!this.currentCharacterId) {
                console.error(
                    "BioController: No character ID available from any source, cannot load bio data",
                );
                return;
            }
        }

        console.log(
            `BioController: Loading bio data for model ID: ${this.currentCharacterId}`,
        );

        // Also update the bio button image using characterData if available
        this.updateBioButtonImage();

        // Get bio data based on model ID
        const bioData = this.bioDataManager.getBioData(this.currentCharacterId);
        if (!bioData) {
            console.warn(
                `No bio data found for character model: ${this.currentCharacterId}`,
            );
            return;
        }

        // Check if we got default data or character-specific data
        const isDefaultData =
            bioData === this.bioDataManager.characterBioData.default;

        // Debug log of available bio data
        console.log("BioController: Found bio data:", {
            name: bioData.fullName,
            modelId: this.currentCharacterId,
            isDefaultData: isDefaultData,
        });

        // Update bio panel title with character name
        this.updateSection("bio-character-name", bioData.fullName);

        // Update the character image in the bio section
        const characterImage = document.getElementById("bio-character-image");
        if (characterImage) {
            // Try to get image from the same source as the bio button image
            if (this.bioButtonImage && this.bioButtonImage.src) {
                characterImage.src = this.bioButtonImage.src;
                characterImage.alt = bioData.fullName;
            } else if (bioData.imageUrl) {
                // Fallback to image URL in bio data if available
                characterImage.src = bioData.imageUrl;
                characterImage.alt = bioData.fullName;
            }
        }

        // Update core info section
        let coreInfo = `
            <p><strong>Lifespan:</strong> ${bioData.lifespan}</p>
            <p><strong>Profession:</strong> ${bioData.profession}</p>
            <p><strong>Nationality:</strong> ${bioData.nationality}</p>
        `;
        this.updateHTML("bio-core-info", coreInfo);

        // Use the new earlyLife field or fall back to biography if not available
        this.updateSection(
            "bio-early-life",
            bioData.earlyLife || bioData.biography,
        );

        // Use new careerHighlights field or fall back to achievements
        const careerContent = bioData.careerHighlights || bioData.achievements;
        const careerList = careerContent
            .map((item) => `<li>${item}</li>`)
            .join("");
        this.updateHTML("bio-career", `<ul>${careerList}</ul>`);

        // Update personal life section with new field
        this.updateSection("bio-personal", bioData.personalLife || "");

        // Update legacy section with new field
        this.updateSection("bio-legacy", bioData.legacy || "");

        // Update quotes
        const quotes = [
            {
                text: bioData.quote,
                author: bioData.fullName,
            },
        ];
        this.updateQuotes(quotes);

        // Update fun facts
        this.updateFunFacts(bioData.funFacts);

        // Add anecdotes section if available
        if (bioData.anecdotes && bioData.anecdotes.length > 0) {
            const anecdotesList = bioData.anecdotes
                .map((anecdote) => `<li>${anecdote}</li>`)
                .join("");
            this.updateHTML("bio-anecdotes", `<ul>${anecdotesList}</ul>`);
        }

        // Add overview section if available
        if (bioData.overview) {
            this.updateSection("bio-overview", bioData.overview);
        }

        console.log(`Loaded bio data for model ID: ${this.currentCharacterId}`);
    }

    /**
     * Update bio content when panel is opened or character changes
     */
    updateBioContent() {
        // If the panel is visible, refresh content
        if (this.bioPanel.classList.contains("active")) {
            this.loadBioData();
        }
    }

    /**
     * Update a section with text content
     *
     * @param {string} elementId - ID of the element to update
     * @param {string} content - Content to set
     */
    updateSection(elementId, content) {
        const element = document.getElementById(elementId);
        if (element && content) {
            element.textContent = content;
        }
    }

    /**
     * Update a section with HTML content
     *
     * @param {string} elementId - ID of the element to update
     * @param {string} htmlContent - HTML content to set
     */
    updateHTML(elementId, htmlContent) {
        const element = document.getElementById(elementId);
        if (element && htmlContent) {
            element.innerHTML = htmlContent;
        }
    }

    /**
     * Update quotes section with array of quotes
     *
     * @param {Array} quotes - Array of quote objects with text and optional author
     */
    updateQuotes(quotes) {
        const quotesContainer = document.getElementById("bio-quotes");
        if (!quotesContainer || !quotes || !quotes.length) return;

        // Clear existing quotes
        quotesContainer.innerHTML = "";

        // Add new quotes
        quotes.forEach((quote) => {
            const quoteElement = document.createElement("div");
            quoteElement.className = "bio-quote";

            quoteElement.textContent = quote.text;

            // Add author attribution if provided
            if (quote.author) {
                const authorElement = document.createElement("div");
                authorElement.className = "bio-quote-author";
                authorElement.textContent = `— ${quote.author || this.currentCharacterName}`;
                quoteElement.appendChild(authorElement);
            }

            quotesContainer.appendChild(quoteElement);
        });
    }

    /**
     * Update fun facts section with array of facts
     *
     * @param {Array} facts - Array of fact strings
     */
    updateFunFacts(facts) {
        const factsContainer = document.getElementById("bio-fun-facts");
        if (!factsContainer || !facts || !facts.length) return;

        // Clear existing facts
        factsContainer.innerHTML = "";

        // Add new facts
        facts.forEach((fact) => {
            const factElement = document.createElement("li");
            factElement.textContent = fact;
            factsContainer.appendChild(factElement);
        });
    }

    /**
     * Toggle the bio panel visibility
     *
     * @param {boolean} show - Whether to show or hide the panel
     */
    toggleBioPanel(show) {
        if (show) {
            // Update the panel state
            this.isPanelActive = true;

            console.log("BioController: Panel opened, refreshing bio data");

            // If we don't have a character ID, try to get the current model ID from storage
            if (!this.currentCharacterId) {
                const storedModelId = localStorage.getItem("selectedModelId");
                if (storedModelId) {
                    console.log(
                        `BioController: No character ID set, using model ID from localStorage: ${storedModelId}`,
                    );
                    this.currentCharacterId = storedModelId;
                }
            }

            // Always refresh bio data when showing the panel
            this.loadBioData();

            // Reset width property for responsive screens
            let defaultWidth = "30%"; // Desktop default (30%)
            if (window.innerWidth > 1400) {
                defaultWidth = "30%";
            } else if (window.innerWidth > 1200) {
                defaultWidth = "30%"; // Tablet default (30%)
            } else if (window.innerWidth > 992) {
                defaultWidth = "50%"; // Tablet default (30%)
            } else if (window.innerWidth > 768) {
                defaultWidth = "50%"; // Mobile default (50%)
            } else if (window.innerWidth > 576) {
                defaultWidth = "80%"; // Tablet default (30%)
            } else {
                defaultWidth = "100%"; // Mobile default (30%)
            }

            this.bioPanel.style.height = "100vh"; // Full height
            this.bioPanel.style.width = defaultWidth;
            this.bioPanel.style.backgroundColor = "rgba(58,65,73, 0.25)";
            this.bioPanel.style.opacity = "1";
            this.bioPanel.classList.add("active");

            // Add active class to the bio button to show toggle state
            this.bioButton.classList.add("bio-button-active");

            // Set the panel position
            this.bioPanel.style.transform = "translateX(0)";
        } else {
            // Update the panel state
            this.isPanelActive = false;

            // Remove active class from the bio button
            this.bioButton.classList.remove("bio-button-active");

            // Add smooth sliding animation when closing with button
            this.bioPanel.style.transition =
                "transform 0.9s cubic-bezier(0.23, 1, 0.32, 1), opacity 0.7s ease, width 0.5s ease";

            // Apply a "sliding right" effect
            this.bioPanel.style.transform = "translateX(110%)"; // Slide to the right
            this.bioPanel.style.opacity = "0.2"; // Fade as it slides

            // Remove active class and reset after animation completes
            setTimeout(() => {
                this.bioPanel.classList.remove("active");
                this.bioPanel.style.transform = "translateX(100%)"; // Reset to standard closed position
                this.bioPanel.style.opacity = "0"; // Reset opacity

                // Reset and remove transition after panel is closed
                setTimeout(() => {
                    this.bioPanel.style.transition = ""; // Remove transition
                }, 200);
            }, 950);
        }
    }

    /**
     * Update the Bio button image using the current character data
     * This ensures the bio button always shows the current character's image
     */
    updateBioButtonImage() {
        if (!this.currentCharacterId || !this.bioButtonImage) {
            return;
        }

        // Try to get image URL from characterData
        if (characterData && characterData[this.currentCharacterId]) {
            const character = characterData[this.currentCharacterId];
            if (character.imageUrl) {
                this.bioButtonImage.src = character.imageUrl;
                this.bioButtonImage.alt = `${character.name} Bio`;
                console.log(
                    `BioController: Updated bio button image for ${character.name}`,
                );
            }
        } else {
            console.warn(
                `BioController: No character data found for model ID: ${this.currentCharacterId}`,
            );
        }
    }
}
